import React from "react";
import { Link, useNavigate } from "react-router-dom";

import FundedProjectSupplyMetrics from "./FundedProjectSupplyMetrics";
import FundedProjectVisual from "./FundedProjectVisual";
import ProjectPriceUpdatePanel from "./ProjectPriceUpdatePanel";
import { formatShareQty } from "../../utils/investorRedeemEligibility";
import { projectLandingPath } from "../../utils/projectViews";

/**
 * Funded project card — chart (left), metrics (center-right), project visual (far right).
 */
export default function FundedProjectMarketCard({
  project,
  priceSeries = [],
  onRefresh,
  refreshing = false,
  mySharesHeld = 0,
  canTransfer = false,
  canRedeem = false,
  transferDisabledReason = "",
  redeemDisabledReason = "",
  onTransfer,
  onRedeem,
  onSharesCertificate,
  showTradeActions = true,
  /** Cap-table strip + “your holding” line (hide when already shown above, e.g. portfolio toggle). */
  showSupplyMetrics = true,
  /** Project title row (hide when parent toggle already shows the name). */
  showProjectTitle = true,
  /** Right-rail project visual (hide when redundant inside a named holding toggle). */
  showProjectVisual = true,
}) {
  const navigate = useNavigate();
  const hasHolding = Number(mySharesHeld) > 0;
  const knowMoreHref = projectLandingPath(project);

  const certificateBtn = onSharesCertificate ? (
    <button
      type="button"
      className="btn-secondary compact-btn redeem-eligible-list__btn investor-funded-card__action"
      title="Preview and download your project-wise share certificate"
      onClick={onSharesCertificate}
    >
      Shares Certificate
    </button>
  ) : null;

  const knowMoreBtn = knowMoreHref ? (
    <Link
      to={knowMoreHref}
      className="btn-secondary compact-btn investor-funded-card__about"
      title="Open the project landing page"
    >
      Know more..
    </Link>
  ) : null;

  const showActions = showTradeActions || (hasHolding && certificateBtn) || knowMoreBtn;

  const actions = showActions ? (
    <div className="investor-funded-card__actions">
      {showTradeActions ? (
        hasHolding ? (
          <>
            <button
              type="button"
              className="btn-secondary compact-btn redeem-eligible-list__btn investor-funded-card__action"
              disabled={!canTransfer}
              title={!canTransfer ? transferDisabledReason || "Transfer not available" : "Transfer shares to another investor"}
              onClick={onTransfer}
            >
              Transfer Shares
            </button>
            <button
              type="button"
              className="btn-secondary compact-btn redeem-eligible-list__btn investor-funded-card__action"
              disabled={!canRedeem}
              title={!canRedeem ? redeemDisabledReason || "Redemption not available" : "Request redemption to treasury"}
              onClick={onRedeem}
            >
              Redeem Shares
            </button>
            {certificateBtn}
          </>
        ) : (
          <p className="investor-funded-card__no-holding muted">
            You do not hold shares in this project yet. Buy shares during fundraising or receive a transfer to use
            transfer or redeem.
          </p>
        )
      ) : (
        certificateBtn
      )}
      {knowMoreBtn}
    </div>
  ) : null;

  return (
    <ProjectPriceUpdatePanel
      variant="compact"
      project={project}
      priceSeries={priceSeries}
      onRefresh={onRefresh}
      refreshing={refreshing}
      holdingShares={showSupplyMetrics && hasHolding ? formatShareQty(mySharesHeld) : null}
      headerExtra={showSupplyMetrics ? <FundedProjectSupplyMetrics project={project} /> : null}
      showProjectTitle={showProjectTitle}
      trailingAside={
        showProjectVisual ? (
          <FundedProjectVisual
            project={project}
            onViewOverview={knowMoreHref ? () => navigate(knowMoreHref) : undefined}
          />
        ) : null
      }
      actions={actions}
    />
  );
}
